import { Box, Button, CircularProgress, Typography, useTheme, Alert, Paper } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Html5QrcodeScanner } from "html5-qrcode";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

import { tokens } from "../../../theme/theme";

/**
 * Reusable QR Code Scanner Component
 *
 * Props:
 * @param {string} stage - Stage identifier ('validation', 'processing', 'confirmation')
 * @param {function} onScanSuccess - Callback function when QR is successfully scanned
 * @param {function} onScanError - Callback function when scan fails
 * @param {boolean} stopOnSuccess - Whether to stop scanning after first successful scan (default: true)
 * @param {boolean} autoStart - Whether to start scanning on component mount (default: true)
 * @param {string} facingMode - Camera facing mode ('environment' or 'user', default: 'environment')
 * @param {number} fps - Frames per second for scanning (default: 10)
 * @param {string} qrCodeSuccessMessage - Custom success message (default: 'QR Code Scanned Successfully')
 */
const QRCodeScanner = ({
  stage = 'validation',
  onScanSuccess,
  onScanError,
  stopOnSuccess = true,
  autoStart = true,
  facingMode = 'environment',
  fps = 10,
  qrCodeSuccessMessage = 'QR Code Scanned Successfully'
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [scannerReady, setScannerReady] = useState(false);
  const [isScanning, setIsScanning] = useState(autoStart);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [lastScannedCode, setLastScannedCode] = useState("");
  const [scanCount, setScanCount] = useState(0);
  const scannerRef = useRef(null);
  const html5QrcodeRef = useRef(null);
  const isProcessingRef = useRef(false);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialize HTML5 QR Code Scanner
  useEffect(() => {
    const initScanner = async () => {
      try {
        // Check if container exists
        const container = document.getElementById("qr-reader");
        if (!container) {
          console.error("[QRCodeScanner] Container 'qr-reader' not found in DOM");
          setErrorMessage("Camera container not found. Please refresh the page.");
          return;
        }
        console.log("[QRCodeScanner] Container found, proceeding with initialization");

        // Check browser support for camera access
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          console.error("[QRCodeScanner] Browser does not support camera access");
          setErrorMessage("Your browser does not support camera access. Try a different browser (Chrome, Firefox, Edge).");
          return;
        }
        console.log("[QRCodeScanner] Browser supports camera access");

        // Check available devices
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const cameras = devices.filter(d => d.kind === 'videoinput');
          console.log("[QRCodeScanner] Available cameras:", cameras.length);
          if (cameras.length === 0) {
            console.warn("[QRCodeScanner] No camera devices found");
            setErrorMessage("No camera device found. Please check your hardware.");
            const err = new Error("NotFoundError: No camera devices available");
            if (onScanError) {
              onScanError(err, stage);
            }
            return;
          }
        } catch (enumError) {
          console.error("[QRCodeScanner] Error enumerating devices:", enumError);
        }

        console.log("[QRCodeScanner] Creating Html5QrcodeScanner instance");
        const scanner = new Html5QrcodeScanner(
          "qr-reader",
          {
            fps: fps,
            qrbox: { width: 250, height: 250 },
            facingMode: facingMode,
            disableFlip: false
          },
          false
        );

        html5QrcodeRef.current = scanner;
        console.log("[QRCodeScanner] Scanner instance created, calling render()");

        const handleSuccess = (decodedText, decodedResult) => {
          // Prevent duplicate processing within 500ms
          if (isProcessingRef.current) {
            return;
          }

          console.log("[QRCodeScanner] QR Code detected:", decodedText);
          isProcessingRef.current = true;
          setLastScannedCode(decodedText);
          setScanCount(prev => prev + 1);
          setSuccessMessage(qrCodeSuccessMessage);

          // Call the success callback
          if (onScanSuccess) {
            onScanSuccess(decodedText, stage);
          }

          // Stop scanning if configured
          if (stopOnSuccess) {
            stopScanning();
          }

          // Reset processing flag after delay
          setTimeout(() => {
            isProcessingRef.current = false;
          }, 500);
        };

        const handleError = (error) => {
          // Only log errors that are not "No code detected" (too verbose)
          if (
            error &&
            error.toString() &&
            !error.toString().includes("No code found in image") &&
            !error.toString().includes("QR code parse error") &&
            !error.toString().includes("not readable")
          ) {
            console.warn("[QRCodeScanner] Scan error:", error);
          }

          // Call error callback only for critical errors
          if (
            error &&
            error.toString() &&
            error.toString().includes("NotAllowedError")
          ) {
            console.error("[QRCodeScanner] Camera permission denied");
            setErrorMessage("Camera access denied. Please check permissions.");
            if (onScanError) {
              onScanError(error, stage);
            }
          }
        };

        if (isScanning) {
          console.log("[QRCodeScanner] isScanning is true, calling scanner.render()");
          await scanner.render(handleSuccess, handleError);
          console.log("[QRCodeScanner] Scanner render() completed successfully");
          setScannerReady(true);
        }

        return () => {
          console.log("[QRCodeScanner] Cleanup: clearing scanner");
          try {
            scanner.clear();
          } catch (e) {
            console.warn("[QRCodeScanner] Error during cleanup:", e);
          }
          // Set scanner to null to prevent accessing stale reference
          html5QrcodeRef.current = null;
        };
      } catch (error) {
        console.error("[QRCodeScanner] Initialization error:", error);
        console.error("[QRCodeScanner] Error name:", error?.name);
        console.error("[QRCodeScanner] Error message:", error?.message);
        console.error("[QRCodeScanner] Full error:", JSON.stringify(error, null, 2));

        // Provide more specific error messages
        let errorMsg = "Failed to initialize camera. Please check browser permissions.";

        if (error?.message?.includes("NotAllowedError")) {
          errorMsg = "Camera access denied. Please allow camera access in browser settings.";
        } else if (error?.message?.includes("NotFoundError")) {
          errorMsg = "No camera device found. Please check your hardware.";
        } else if (error?.message?.includes("NotReadableError")) {
          errorMsg = "Camera is in use by another application. Please close it first.";
        } else if (error?.message?.includes("SecurityError")) {
          errorMsg = "Camera access blocked for security reasons. Use HTTPS or localhost.";
        } else if (error?.message) {
          errorMsg = error.message;
        }

        setErrorMessage(errorMsg);
        if (onScanError) {
          onScanError(error, stage);
        }
      }
    };

    if (isScanning) {
      // Small delay to ensure DOM is ready
      console.log("[QRCodeScanner] Scheduling scanner initialization with 100ms delay");
      const timer = setTimeout(() => {
        console.log("[QRCodeScanner] Starting initialization");
        initScanner();
      }, 100);

      return () => clearTimeout(timer);
    }

    return () => {
      if (html5QrcodeRef.current) {
        try {
          console.log("[QRCodeScanner] Cleaning up scanner reference");
          html5QrcodeRef.current.clear();
        } catch (e) {
          console.warn("Error clearing scanner:", e);
        }
      }
    };
  }, [isScanning, fps, facingMode, onScanSuccess, onScanError, stopOnSuccess, qrCodeSuccessMessage, stage]);

  // Start scanning
  const startScanning = async () => {
    try {
      setErrorMessage("");
      setSuccessMessage("");
      setScanCount(0);
      setLastScannedCode("");

      // Clear any existing scanner before starting
      if (html5QrcodeRef.current) {
        try {
          await html5QrcodeRef.current.clear();
        } catch (e) {
          console.warn("Error clearing old scanner:", e);
        }
        html5QrcodeRef.current = null;
      }

      setIsScanning(true);
    } catch (error) {
      console.error("[QRCodeScanner] Start error:", error);
      setErrorMessage("Failed to start camera. Please try again.");
    }
  };

  // Stop scanning
  const stopScanning = async () => {
    try {
      if (html5QrcodeRef.current) {
        try {
          await html5QrcodeRef.current.clear();
        } catch (clearError) {
          console.warn("[QRCodeScanner] Error clearing scanner:", clearError);
        }
        html5QrcodeRef.current = null;
      }
      setIsScanning(false);
    } catch (error) {
      console.error("[QRCodeScanner] Stop error:", error);
    }
  };

  // Clear messages
  const clearMessages = () => {
    setErrorMessage("");
    setSuccessMessage("");
  };

  return (
    <Paper
      sx={{
        backgroundColor: colors.primary[400],
        p: 3,
        borderRadius: 2,
        boxShadow: theme.shadows[3],
        textAlign: "center"
      }}
    >
      {/* Stage indicator */}
      <Box mb={2}>
        <Typography variant="caption" sx={{ color: colors.greenAccent[400], fontWeight: "bold" }}>
          STAGE: {stage.toUpperCase()}
        </Typography>
      </Box>

      {/* Camera container */}
      <Box
        id="qr-reader"
        sx={{
          width: "100%",
          borderRadius: 2,
          overflow: "hidden",
          mb: 2,
          minHeight: "300px",
          backgroundColor: colors.primary[500],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          border: `2px solid ${colors.primary[300]}`
        }}
      >
        {!isScanning && (
          <Box sx={{ textAlign: "center", p: 3 }}>
            <CameraAltIcon sx={{ fontSize: 60, color: colors.grey[300], mb: 1 }} />
            <Typography color="textSecondary">
              Click "Start Scanning" to begin
            </Typography>
          </Box>
        )}
      </Box>

      {/* Status messages */}
      {errorMessage && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={clearMessages}
          action={
            !isScanning && (
              <Button
                color="inherit"
                size="small"
                onClick={startScanning}
                sx={{ ml: 1 }}
              >
                RETRY
              </Button>
            )
          }
        >
          {errorMessage}
        </Alert>
      )}

      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={clearMessages}
        >
          {successMessage}
          {lastScannedCode && (
            <>
              <br />
              <strong>QR Code:</strong> {lastScannedCode}
            </>
          )}
        </Alert>
      )}

      {/* Scan statistics */}
      {scanCount > 0 && (
        <Box mb={2}>
          <Typography variant="body2" color="textSecondary">
            Scans attempted: {scanCount} | Last code: {lastScannedCode || "None"}
          </Typography>
        </Box>
      )}

      {/* Loading indicator */}
      {isScanning && !scannerReady && (
        <Box mb={2} display="flex" justifyContent="center" alignItems="center" gap={1}>
          <CircularProgress size={24} />
          <Typography variant="body2">Initializing camera...</Typography>
        </Box>
      )}

      {/* Control buttons */}
      <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
        {!isScanning ? (
          <Button
            variant="contained"
            color="success"
            startIcon={<PlayCircleIcon />}
            onClick={startScanning}
          >
            Start Scanning
          </Button>
        ) : (
          <Button
            variant="contained"
            color="error"
            startIcon={<StopCircleIcon />}
            onClick={stopScanning}
          >
            Stop Scanning
          </Button>
        )}
      </Box>

      {/* Additional info */}
      <Box mt={2}>
        <Typography variant="caption" color="textSecondary">
          Position QR code within the frame • Ensure good lighting
        </Typography>
      </Box>
    </Paper>
  );
};

export default QRCodeScanner;
